<template>
    <HeaderPanel />
    <Toast />
    <div class="admin-container">
        <Button type="button" icon="pi pi-ellipsis-v" @click="toggle" aria-haspopup="true"
            aria-controls="overlay_menu" class="p-button-rounded p-button-text" />
        <Menu  id="overlay_menu" :model="items" :popup="true" />
        <Popover ref="menu" >
            <Listbox v-model="selectedItem" :options="items" optionLabel="label" optionValue="label" :popup="true" />
        </Popover>

        <Card 
            v-if="selectedItem === 'Пользователи'" 
            class="register-card"
            style="align-items: center; width: 100%;"
        >
            <template #title>
                <h2 class="text-center">Управление пользователями</h2>
            </template>
            <template #content>
                <UserManagement />
            </template>
        </Card>
        <Card v-if="selectedItem === 'База данных'" class="database-card" style="align-items: center; width: 100%;">
            <template #title>
                <h2 class="text-center">База данных</h2>
            </template>
            <template #content>
                <DataBase />
            </template>
        </Card>
        <Card v-if="selectedItem === 'Google Access'" class="google-access-card" style="align-items: center; width: 100%;">
            <template #title>
                <h2 class="text-center">Google Access</h2>
            </template>
            <template #content>
                <GoogleAccess />
            </template>
        </Card>
        <Card v-if="selectedItem === 'API Analytics'" class="database-card" style="align-items: center; width: 100%;">
            <template #title>
                <h2 class="text-center">API Analytics</h2>
            </template>
            <template #content>
                <ApiAnalytics />
            </template>
        </Card>
        <Card v-if="selectedItem === 'Обложки и баннеры'" class="database-card" style="align-items: center; width: 100%;">
            <template #title>
                <h2 class="text-center">Обложки и баннеры</h2>
            </template>
            <template #content>
                <ThemeImages />
            </template>
        </Card>
    </div>
</template>

<script>
import axios from 'axios';
import Menu from 'primevue/menu';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import HeaderPanel from '@/components/HeaderPanel.vue';
import DataBase from '@/components/AdminPage/DataBase.vue';
import GoogleAccess from '@/components/AdminPage/GoogleAccess.vue';
import UserManagement from '@/components/AdminPage/UserManagement.vue';
import ApiAnalytics from '@/components/AdminPage/ApiAnalytics.vue';
import ThemeImages from '@/components/AdminPage/ThemeImages.vue';
import Toast from 'primevue/toast';
import Button from 'primevue/button';
import Card from 'primevue/card';
import { Listbox } from 'primevue';
import Popover from 'primevue/popover';
import { useUserStore } from '@/stores/user';

export default {
    components: {
        Menu,
        HeaderPanel,
        DataBase,
        Toast,
        Button,
        Card,
        Listbox,
        Popover,
        GoogleAccess,
        UserManagement,
        ApiAnalytics,
        ThemeImages
    },
    data() {
        return {
            selectedItem: null,
            allItems: [
                { label: 'Пользователи', icon: 'pi pi-users', requiresAdmin: true },
                { label: 'База данных', icon: 'pi pi-database', requiresDbAccess: true },
                { label: 'Google Access', icon: 'pi pi-google', requiresAdmin: true },
                { label: 'API Analytics', icon: 'pi pi-chart-line', requiresDbAccess: true },
                { label: 'Обложки и баннеры', icon: 'pi pi-image', requiresDbAccess: true }
            ],
            roles: [
                { id: 1, description: 'Гость' },
                { id: 2, description: 'Сотрудник' }, 
                { id: 3, description: 'Администратор' },
                { id: 4, description: 'Подрядчик' }
            ],
            showRegister: false,
            formData: {
                username: '',
                email: '',
                password: '',
                role: null
            },
            errors: {},
            resolver: zodResolver(
                z.object({
                    username: z.string().min(1, { message: 'Username is required.' }),
                    email: z.string().email({ message: 'Invalid email address.' })
                })
            )
        }
    },
    computed: {
        userStore() {
            return useUserStore()
        },
        // Фильтруем пункты меню на основе разрешений из БД
        items() {
            return this.allItems.filter(item => {
                // "Пользователи" - нужен edit на admin_panel
                if (item.label === 'Пользователи') {
                    return this.userStore.canManageUsers
                }
                // "База данных" - нужен view на admin_panel
                if (item.label === 'База данных') {
                    return this.userStore.hasAdminAccess
                }
                // "Google Access" - нужен edit на admin_panel
                if (item.label === 'Google Access') {
                    return this.userStore.canManageGoogleAccess
                }
                if (item.label === 'API Analytics') {
                    return this.userStore.hasAdminAccess
                }
                // «Обложки и баннеры» — отдельное право theme_images/view
                if (item.label === 'Обложки и баннеры') {
                    return this.userStore.hasPermission('theme_images', 'view')
                }
                return false
            })
        }
    },
    async mounted() {
        // Убеждаемся, что пользователь загружен
        if (!this.userStore.user && this.userStore.token) {
            await this.userStore.checkAuth()
        }
        // Устанавливаем первый доступный пункт меню по умолчанию
        if (this.items.length > 0 && !this.selectedItem) {
            this.selectedItem = this.items[0].label
        }
    },
    methods: {
        toggle(event) {
            this.$refs.menu.toggle(event);
        },
        async newUser() {
            this.errors = {};
            
            // Валидация полей
            if (!this.formData.username) {
                this.errors.username = 'Имя пользователя обязательно';
            }
            if (!this.formData.email) {
                this.errors.email = 'Email обязателен';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
                this.errors.email = 'Некорректный email';
            }
            if (!this.formData.role) {
                this.errors.role = 'Выберите роль';
            }
            if (this.formData.password.length > 0 && this.formData.password.length < 8) {
                this.errors.password = 'Пароль должен быть не менее 8 символов';
            }

            if (Object.keys(this.errors).length > 0) {
                return;
            }

            try {
                const response = await axios.post("https://ncht.bz/api/auth/new-user", {
                    username: this.formData.username,
                    email: this.formData.email,
                    password: this.formData.password,
                    role: this.formData.role.id
                });

                if (response.status === 200) {
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Успешно', 
                        detail: 'Пользователь успешно зарегистрирован', 
                        life: 3000 
                    });
                    this.showRegister = false;
                    this.formData = {
                        username: '',
                        email: '',
                        password: '',
                        role: null
                    };
                    /*const registerResponse = await axios.post("https://ncht.bz/api/mail/send-register-mail", { 
                        link: `https://ncht.bz/login/${btoa(this.formData.username)}`, 
                        username: this.formData.username 
                    });
                    
                    if (registerResponse.status === 200) {
                        this.$toast.add({ 
                            severity: 'success', 
                            summary: 'Успешно', 
                            detail: 'Пользователь успешно зарегистрирован', 
                            life: 3000 
                        });
                        this.showRegister = false;
                        this.formData = {
                            username: '',
                            email: '',
                            password: '',
                            role: null
                        };
                    }*/
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors;
                } else {
                    this.$toast.add({ 
                        severity: 'error', 
                        summary: 'Ошибка', 
                        detail: error.response?.data?.message || 'Произошла ошибка при регистрации', 
                        life: 3000 
                    });
                }
            }
        },
        showToast(message) {
            this.$toast.add({ severity: 'info', summary: 'Info', detail: message, life: 3000 });
        }
    },
    watch: {
        async showRegister(newVal) {
            if (newVal) {
                const response = await axios.get("https://ncht.bz/api/auth/roles");
                this.roles = response.data;
            }
        }
    }
}
</script>

<style scoped>
.admin-container {
    padding: 2rem;
}

.register-card {
    width: 100%;
    max-width: 1200px;
    margin: 2rem auto;
}

.field {
    margin-bottom: 1.5rem;
    width: 50%;
}

.text-center {
    text-align: center;
}

:deep(.p-card-body) {
    width: 100%;
    height: 100%;
}

:deep(.p-card) {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

:deep(.p-float-label) {
    margin-bottom: 0.5rem;
}

:deep(.p-error) {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.google-access-card {
    /*max-width: 500px;*/
    margin: 2rem auto;
}
</style>

